<template>
  <div class="case-page" :class="{ 'is-mobile': isMobileRoute }">
    <section v-if="!isMobileRoute" class="case-hero">
    </section>

    <div class="case-shell">
      <section class="case-grid">
        <template v-if="isPageLoading">
          <article v-for="index in 9" :key="`case-skeleton-${index}`" class="case-card case-card-skeleton"
            aria-hidden="true">
            <div class="case-card-cover">
              <div class="case-card-cover-loading"></div>
            </div>
          </article>
        </template>

        <template v-else>
          <article v-for="item in pagedCases" :key="item.id" class="case-card" @click="openDetail(item)">
            <div class="case-card-cover">
              <div v-if="!isCoverLoaded(item)" class="case-card-cover-loading"></div>
              <img :src="item.coverImg" :alt="item.title" loading="lazy" :class="{ loaded: isCoverLoaded(item) }"
                @load="markCoverLoaded(item)" @error="handleCoverError($event, item)">
              <div v-if="item.tagText" class="case-card-tags-line">{{ item.tagText }}</div>
            </div>

            <div class="case-card-body">
              <h3 class="case-card-title">
                <span v-if="item.isOrder" class="diamand">
                  <img src="https://obs.huahuaeco.com/official/diamand-logo.png" alt="化化钻石会员">
                  <span>平台交易案例</span>
                </span>
                <span>{{ item.title }}</span>
              </h3>

              <div v-if="!isMobileRoute && item.tagText" class="case-card-meta">{{ item.tagText }}</div>
            </div>
          </article>
        </template>
      </section>

      <div v-if="isMobileRoute && !isPageLoading" class="load-more-area">
        <div v-if="loadingMore" class="load-more-text">加载中...</div>
        <div v-else-if="!hasMore && caseList.length > 0" class="load-more-text">---没有更多了---</div>
      </div>

      <div v-if="pageCount > 1 && !isMobileRoute && !isPageLoading" class="pagination-bar">
        <button type="button" class="page-btn nav-btn" :disabled="pageNum === 1" @click="changePage(pageNum - 1)">
          上一页
        </button>

        <button v-for="page in pageCount" :key="page" type="button" class="page-btn number-btn"
          :class="{ active: pageNum === page }" @click="changePage(page)">
          {{ page }}
        </button>

        <button type="button" class="page-btn nav-btn" :disabled="pageNum === pageCount"
          @click="changePage(pageNum + 1)">
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue'
import { getUserCasePageList } from '@/api/case'
import { getPageSsgState, setPageSsgState, shouldMarkImageLoaded } from '@/utils/ssg-state'

const SSG_STATE_KEY = 'casePage'

const CASE_SEEDS = [
  {
    prefix: '现代简约横厅案例',
    tags: ['69平现代轻奢', '半包装修', '深圳案例', '实景交付']
  },
  {
    prefix: '奶油风客餐厅改造案例',
    tags: ['89平旧房翻新', '全包装修', '南山案例', '温柔奶油风']
  },
  {
    prefix: '原木中古风落地案例',
    tags: ['118平改善型住宅', '设计施工一体', '福田案例', '收纳升级']
  },
  {
    prefix: '小户型高效收纳案例',
    tags: ['56平两居', '局部改造', '龙岗案例', '预算友好']
  },
  {
    prefix: '轻奢大平层实景案例',
    tags: ['143平大平层', '全案设计', '宝安案例', '硬软装一体']
  }
]

export default {
  name: 'CasePage',
  setup() {
    useHead({
      title: '装修案例-自建房案例-设计效果图与真实业主完工实景-化化网',
      meta: [
        { name: 'description', content: '化化网真实装修案例库、自建房案例库，覆盖旧房翻新、新房整装、局部改造、自建房翻新与新建等。看效果图、实景图，读设计理念，获取设计灵感。' },
        { name: 'keywords', content: '装修案例,实景案例,旧房翻新案例,自建房案例,化化网' }
      ]
    })
  },
  data() {
    return {
      pageNum: 1,
      pageSize: 30,
      caseList: [],
      count: 0,
      isPageLoading: true,
      loadingMore: false,
      coverLoadedMap: {}
    }
  },
  computed: {
    isMobileRoute() {
      return this.$route.path === '/m/case'
    },
    caseDetailBasePath() {
      return this.isMobileRoute ? '/m/case/detail' : '/case/detail'
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.count / this.pageSize))
    },
    pagedCases() {
      return this.caseList
    },
    hasMore() {
      return this.caseList.length < this.count
    }
  },
  created() {
    this.applySsgState()
  },
  mounted() {
    this.refreshCaseList()
    if (this.isMobileRoute) {
      window.addEventListener('scroll', this.handleMobileScroll, { passive: true })
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleMobileScroll)
  },
  serverPrefetch() {
    return this.getUserCasePageListApi()
  },
  methods: {
    applySsgState() {
      const state = getPageSsgState(this.$route, SSG_STATE_KEY)
      if (!state) {
        return
      }

      this.pageNum = state.pageNum || 1
      this.pageSize = state.pageSize || 30
      this.caseList = Array.isArray(state.caseList) ? state.caseList : []
      this.count = state.count || 0
      this.coverLoadedMap = state.coverLoadedMap || {}
    },
    persistSsgState() {
      setPageSsgState(this.$route, SSG_STATE_KEY, {
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        caseList: this.caseList,
        count: this.count,
        coverLoadedMap: this.coverLoadedMap
      })
    },
    async getUserCasePageListApi({ append = false } = {}) {
      const { pageNum, pageSize } = this
      try {
        const res = await getUserCasePageList({
          pageNum,
          pageSize
        })
        const { data = [], total = 0 } = res.data || {}
        const newCoverMap = {}
        data.forEach(item => {
          item.coverImg = item.coverImg || 'https://obs.huahuaeco.com/official/product2.png'
          item.tags = this.buildCaseTags(item)
          item.tagText = item.tags.join(' | ')
          newCoverMap[item.id] = shouldMarkImageLoaded(item.coverImg)
        })
        if (append) {
          this.caseList = [...this.caseList, ...data]
          this.coverLoadedMap = { ...this.coverLoadedMap, ...newCoverMap }
        } else {
          this.caseList = data || []
          this.coverLoadedMap = newCoverMap
        }
        this.count = total || 0
        this.persistSsgState()
      } catch (err) {
        console.error('fetch case list error', err)
      }
    },
    async refreshCaseList() {
      this.isPageLoading = true
      try {
        await this.getUserCasePageListApi()
      } finally {
        this.isPageLoading = false
      }
    },
    isCoverLoaded(item) {
      if (!item?.id) {
        return true
      }

      return Boolean(this.coverLoadedMap[item.id])
    },
    markCoverLoaded(item) {
      if (!item?.id) {
        return
      }

      this.coverLoadedMap[item.id] = true
    },
    handleCoverError(event, item) {
      const target = event?.target
      const fallbackCover = 'https://obs.huahuaeco.com/official/product2.png'
      if (target && target.getAttribute('src') !== fallbackCover) {
        if (item) {
          item.coverImg = fallbackCover
        }
        target.src = fallbackCover
        return
      }

      if (target) {
        target.style.visibility = 'hidden'
      }
      this.markCoverLoaded(item)
    },
    formatDictValue(value) {
      if (value === undefined || value === null || value === '') return ''
      if (typeof value === 'object') {
        return value.dictLabel || value.label || value.name || value.value || ''
      }
      return value
    },
    safeToFixed(num, decimal = 2) {
      const digits = Number.isInteger(Number(decimal)) && Number(decimal) >= 0 ? Number(decimal) : 2;
      const value = Number(num);
      if (!Number.isFinite(value)) {
        return (0).toFixed(digits);
      }

      const rawText = typeof num === 'string' ? num.trim() : '';
      const normalizedText = rawText && /^[-+]?\d+(?:\.\d+)?$/.test(rawText)
        ? rawText
        : value.toFixed(Math.min(digits + 12, 20)).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');

      const isNegative = normalizedText.startsWith('-');
      const unsignedText = normalizedText.replace(/^[+-]/, '');
      let [integerPart, fractionalPart = ''] = unsignedText.split('.');
      integerPart = integerPart.replace(/^0+(?=\d)/, '') || '0';

      const keptFraction = fractionalPart.slice(0, digits).padEnd(digits, '0');
      const nextDigit = fractionalPart[digits] || '0';
      let mergedValue = `${integerPart}${keptFraction}`.replace(/^0+(?=\d)/, '') || '0';
      let roundedInteger = BigInt(mergedValue);

      if (nextDigit >= '5') {
        roundedInteger += 1n;
      }

      const isZero = roundedInteger === 0n;
      const roundedText = roundedInteger.toString().padStart(digits + 1, '0');
      const roundedIntegerPart = digits === 0 ? roundedText : roundedText.slice(0, -digits) || '0';
      const roundedFractionalPart = digits === 0 ? '' : roundedText.slice(-digits);
      const sign = isNegative && !isZero ? '-' : '';

      if (digits === 0 || /^0+$/.test(roundedFractionalPart)) {
        return `${sign}${roundedIntegerPart}`;
      }

      return `${sign}${roundedIntegerPart}.${roundedFractionalPart}`;
    },
    formatCost(cost) {
      if (cost === undefined || cost === null || cost === '') return ''
      const costText = String(cost).trim()
      if (!costText) return ''
      if (/元|万/.test(costText)) return costText
      const costNumber = Number(costText)
      if (!Number.isFinite(costNumber)) return costText
      // if (costNumber >= 10000) return `${Number((costNumber / 10000).toFixed(2))}万`
      if (costNumber >= 10000) {
        return `${this.safeToFixed(costNumber / 10000)}万元`
      }
      return `${costText}元`
    },
    buildCaseTags(item) {
      const area = item?.acreage ? `${item.acreage}m²` : ''
      const style = this.formatDictValue(item?.designStyle)
      const houseType = item?.houseType || ''
      const cost = this.formatCost(item?.cost)
      const decorationType = this.formatDictValue(item?.decorationType)
      return [area, style, houseType, cost || decorationType].filter(Boolean)
    },
    buildCaseList() {
      return Array.from({ length: 45 }, (_, index) => {
        const seed = CASE_SEEDS[index % CASE_SEEDS.length]
        return {
          id: `case-${index + 1}`,
          title: `${seed.prefix}，空间动线与采光层次同步提升`,
          cover: 'https://obs.huahuaeco.com/official/product2.png',
          tags: seed.tags
        }
      })
    },
    changePage(page) {
      if (page < 1 || page > this.pageCount || page === this.pageNum) return
      this.pageNum = page
      this.refreshCaseList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleMobileScroll() {
      if (!this.isMobileRoute || this.loadingMore || !this.hasMore) {
        return
      }
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMore()
      }
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore) {
        return
      }
      this.loadingMore = true
      this.pageNum += 1
      try {
        await this.getUserCasePageListApi({ append: true })
      } finally {
        this.loadingMore = false
      }
    },
    openDetail(caseItem) {
      this.$router.push({
        path: `${this.caseDetailBasePath}/${caseItem.id}`
      })
    }
  }
}
</script>

<style scoped lang="scss">
.case-page {
  padding: 60px 0 80px;
  min-height: 100vh;
  background: #f4f6fb;
}

.case-hero {
  height: 380px;
  background-image: url('https://obs.huahuaeco.com/official/caseBanner.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.case-hero-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.case-hero-title {
  margin: 0;
  color: #fff;
  font-size: 44px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 4px;
}

.case-shell {
  width: min(1200px, calc(100% - 48px));
  margin: 18px auto 0;
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 22px;
}

.case-card {
  min-width: 0;
  background: #fff;
  border: 1px solid #edf0f5;
  box-shadow: 0 10px 24px rgba(25, 45, 86, 0.06);
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
}

.case-card-skeleton {
  cursor: default;
}

.case-card-cover {
  position: relative;
  overflow: hidden;
  background: #e9e7e4;
}

.case-card-skeleton .case-card-cover {
  aspect-ratio: 1 / 0.64;
}

.case-card-tags-line {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 28px 12px 8px;
  color: #fff;
  font-size: 14px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.62) 100%);
}

.case-card-cover-loading {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0) 100%), #efe7dd;
  background-size: 200% 100%;
  animation: case-card-shimmer 1.2s ease-in-out infinite;
}

.case-card-cover img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 0.64;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.24s ease;
}

.case-card-cover img.loaded {
  opacity: 1;
}

.case-card-body {
  padding: 12px 12px 14px;
}

.case-card-title {
  margin: 0;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  .diamand {
    display: inline-flex;
    vertical-align: middle;
    gap: 4px;
    margin-right: 6px;
    align-items: center;
    background: linear-gradient(0deg, #E8F3FF 0%, #E8F3FF 100%);
    padding: 2px 6px;
    border-radius: 12px;

    img {
      width: 16px;
      height: 16px;
    }

    span {
      color: #4090ff;
      font-size: 12px;
      line-height: 12px;
    }
  }
}

.case-card-meta {
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 36px;
}

.page-btn {
  min-width: 34px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #333;
  font-size: 12px;
  line-height: 26px;
  cursor: pointer;
}

.page-btn.active {
  border-color: #1677ff;
  background: #1677ff;
  color: #fff;
}

.page-btn:disabled {
  color: #bfbfbf;
  cursor: not-allowed;
  background: #fafafa;
}

@keyframes case-card-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.case-page.is-mobile {
  min-height: 100vh;
  padding: calc(88 * var(--rpx)) 0 calc(44 * var(--rpx));
  background: #f4f7fb;

  .case-shell {
    width: 100%;
    margin: 0;
    padding: calc(14 * var(--rpx)) calc(16 * var(--rpx)) 0;
  }

  .load-more-area {
    display: flex;
    justify-content: center;
    padding: calc(24 * var(--rpx)) 0 calc(16 * var(--rpx));
  }

  .load-more-text {
    color: #8c8c8c;
    font-size: calc(24 * var(--rpx));
  }



  .case-grid {
    display: flex;
    flex-direction: column;
    gap: calc(16 * var(--rpx));
  }

  .case-card {
    overflow: hidden;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .case-card-cover {
    border-radius: calc(18 * var(--rpx));
  }

  .case-card-skeleton .case-card-cover {
    aspect-ratio: 1 / 0.58;
    min-height: calc(300 * var(--rpx));
  }

  .case-card-cover img {
    aspect-ratio: 1 / 0.58;
    min-height: calc(300 * var(--rpx));
  }

  .case-card-tags-line {
    padding: calc(42 * var(--rpx)) calc(18 * var(--rpx)) calc(10 * var(--rpx));
    color: #fff;
    font-size: calc(26 * var(--rpx));
    line-height: 1.25;
  }

  .case-card-body {
    padding: calc(14 * var(--rpx)) calc(4 * var(--rpx)) 0;
    background: transparent;
  }

  .case-card-title {
    display: flex;
    align-items: center;
    gap: calc(10 * var(--rpx));
    color: #262626;
    font-size: calc(30 * var(--rpx));
    font-weight: 600;
    line-height: 1.35;
    text-shadow: none;
    line-clamp: 1;
    -webkit-line-clamp: 1;

    .diamand {
      flex: 0 0 auto;
      padding: calc(3 * var(--rpx)) calc(8 * var(--rpx));
      border-radius: calc(999 * var(--rpx));
      background: #e8f3ff;
      text-shadow: none;

      img {
        width: calc(22 * var(--rpx));
        height: calc(22 * var(--rpx));
      }

      span {
        color: #4090ff;
        font-size: calc(18 * var(--rpx));
        line-height: 1;
      }
    }

    >span:last-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

@media (max-width: 1200px) {
  .case-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .case-page {
    padding-top: 76px;
  }

  .case-hero {
    height: 164px;
  }

  .case-hero-title {
    font-size: 30px;
    letter-spacing: 2px;
  }

  .case-shell {
    width: calc(100% - 28px);
  }

  .case-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .case-card-body {
    padding: 10px 10px 12px;
  }

  .pagination-bar {
    flex-wrap: wrap;
  }
}
</style>
